// web/src/features/profile/hooks/usePlayerProfile.ts
//
// Read/update a single player's profile. Hits GET/PUT /players/:id and caches
// under `player.byId` — shared with the player display data (Player page tabs).
// Used by the OLD EditProfileModal (user/player modes). The NEW Edit Player
// modal uses a separate self-scoped hook (useMePlayerProfile) — do not repoint
// this one.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import {
  getPlayerProfile,
  updatePlayerProfile,
  type PlayerProfileResponse,
  type UpdatePlayerPayload,
} from '@sportninja/common/services/player/playerProfile'

const STALE_TIME = 5 * 60 * 1000

/** GET /players/:id — read hook for the (legacy) Edit Profile modal. */
export const usePlayerProfile = (playerId: string | null | undefined) => {
  const { data, isPending, isFetching, error, refetch } =
    useQuery<PlayerProfileResponse>({
      queryKey: queryKeys.player.byId(playerId),
      queryFn: () => getPlayerProfile(playerId as string),
      enabled: Boolean(playerId),
      staleTime: STALE_TIME,
    })

  return {
    data: data ?? null,
    loading: Boolean(playerId) && (isPending || isFetching),
    error: error as Error | null,
    refetch,
  }
}

/** PUT /players/:id — mutation that invalidates the player's cache on success. */
export const useUpdatePlayerProfile = () => {
  const queryClient = useQueryClient()
  return useMutation<
    PlayerProfileResponse,
    Error,
    { playerId: string; payload: UpdatePlayerPayload }
  >({
    mutationFn: ({ playerId, payload }) =>
      updatePlayerProfile(playerId, payload),
    // Override the global mutations.retry=1 default (see queryClient config).
    retry: 0,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.player.byId(variables.playerId),
      })
    },
  })
}
